import { Flex, Spinner, IconButton, Box, Button } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { Link, useLocation } from "react-router-dom";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { GCBigLogoIcon } from "../icons/GCBigLogoIcon";
import { GCSmallLogoIcon } from "../icons/GCSmallLogoIcon";
import { GlobalSearch } from "../layouts/components/GlobalSearch";
import { SearchBarWithDropdown } from "../../screens/search/components/SearchBarWithDropdown";
import { SearchIcon } from "../icons/SearchIcon";
import { ConversationMenu } from "./components/ConversationMenu";
import { LoginMenu } from "./components/LoginMenu.tsx";
import { MenuToggle } from "./components/MenuToggle";
import { NotificationMenu } from "./components/NotificationMenu";
import { useIsOverlayOpen } from "../../screens/reader/hooks/hookstate/useIsOverlayOpen.ts";
import { AccountMenu } from "./components/AccountMenu.tsx";
import { useUserData } from "../../hooks/useUserData.ts";
import { NAVBAR_HEIGHT } from "../../screens/root/constants.ts";
import { useAccountData } from "../../hooks/useAccountData.ts";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore.ts";
import { HtmlParser } from "../../screens/root/HtmlParser.tsx";
import { CloseIcon } from "@chakra-ui/icons";
import {
  isRibbonSnoozed,
  RIBBON_SNOOZE_KEY,
  useShouldShowRibbon,
} from "../../hooks/useRibbonBannerHeight.ts";
import { BROWSE_SEGMENTS } from "../../screens/search/hooks/useSearchContext.tsx";

const slideIn = keyframes`
  0% {
    margin-top: -${NAVBAR_HEIGHT}px;
  }
  100% {
    margin-top: 0px;
  }
`;

const SNOOZE_DURATION_MS = 48 * 60 * 60 * 1000; // 48 hours in milliseconds

const snoozeRibbon = (): void => {
  const snoozedUntil = Date.now() + SNOOZE_DURATION_MS;
  localStorage.setItem(RIBBON_SNOOZE_KEY, snoozedUntil.toString());
};

export function NavBar() {
  const { userData, isFetching } = useUserData();
  const isSmall = useIsSmallBreakpoint();
  const accountData = useAccountData();
  const isSearchV2 = !!accountData?.features?.["search_v2"];
  const { isOverlayOpen: isReaderOverlayOpen } = useIsOverlayOpen();
  const location = useLocation();

  const isValkyraePage = location.pathname.includes("valkyrae");
  const isGoldPage = location.pathname.includes("gold");

  const { content: ribbonContent } = accountData?.ribbon ?? {};

  const shouldShowRibbonBase = useShouldShowRibbon();
  const [isRibbonDismissed, setIsRibbonDismissed] = useState(isRibbonSnoozed);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const isBrowsePage = BROWSE_SEGMENTS.has(location.pathname.split("/")[1] ?? "");

  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchToggleRef = useRef<HTMLDivElement>(null);

  // Auto-open mobile search bar on browse pages or when there's a search query
  const hasSearchQuery = new URLSearchParams(location.search).has("q");
  useLayoutEffect(() => {
    if (isSearchV2 && isSmall && (isBrowsePage || hasSearchQuery)) {
      setIsMobileSearchOpen(true);
    } else if (!isBrowsePage && !hasSearchQuery) {
      setIsMobileSearchOpen(false);
    }
  }, [isBrowsePage, hasSearchQuery, isSearchV2, isSmall]);

  // Sync topBarHeight immediately when search bar toggles to prevent scrollbar flash
  useLayoutEffect(() => {
    const el = topbarRef.current;
    if (el) {
      rootComponentsStore.topBarHeight.set(el.offsetHeight || NAVBAR_HEIGHT);
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    if (!isMobileSearchOpen || isBrowsePage) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mobileSearchRef.current?.contains(e.target as Node) ||
        mobileSearchToggleRef.current?.contains(e.target as Node)
      ) return;
      setIsMobileSearchOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileSearchOpen, isBrowsePage]);

  const handleDismissRibbon = () => {
    setIsAnimatingOut(true);
    // Immediately start updating topBarHeight to animate it down
    rootComponentsStore.topBarHeight.set(NAVBAR_HEIGHT);
    // Wait for animation to complete before actually dismissing
    setTimeout(() => {
      snoozeRibbon();
      setIsRibbonDismissed(true);
    }, 300); // Match animation duration
  };

  const shouldShowRibbon = shouldShowRibbonBase && !isRibbonDismissed;

  const topbarRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = topbarRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      rootComponentsStore.topBarHeight.set(
        entry.contentRect.height || NAVBAR_HEIGHT,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const navBarContent = (
    <Flex direction="column" ref={topbarRef}>
      <Flex
        height={NAVBAR_HEIGHT + "px"}
        minHeight={NAVBAR_HEIGHT + "px"}
        paddingLeft={isSmall ? "10px" : "32px"}
        background="charcoal.charcoal"
        justifyContent="space-between"
        alignItems="center"
        boxShadow="0px 4px 24px rgba(0, 0, 0, 0.4)"
        borderBottom="1px solid"
        borderColor="transparent.white.10"
        animation={isReaderOverlayOpen ? `${slideIn} .1s linear 1` : "none"}
      >
        <Flex direction="row" gap={isSmall ? "0" : "4px"} alignItems="center">
          {!isGoldPage && !isValkyraePage && <MenuToggle />}
          <Link to="/" aria-label="GlobalComix Logo">
            {isSmall ? (
              <GCSmallLogoIcon color="blaze.blaze" boxSize="24px" />
            ) : (
              <GCBigLogoIcon color="blaze.blaze" width="136px" height="26px" />
            )}
          </Link>
        </Flex>
        <Flex
          flex={1}
          height="100%"
          position="relative"
          justifyContent="center"
          alignItems="center"
          px={isSearchV2 ? 10 : 0}
        >
          {isSearchV2 ? (
            !isSmall && <SearchBarWithDropdown />
          ) : (
            !isSmall && <GlobalSearch />
          )}
        </Flex>
        <Flex
          direction="row"
          gap={1}
          height="100%"
          alignItems="center"
          paddingRight={isSmall ? "16px" : "38px"}
        >
          {isFetching ? (
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              width="146px"
            >
              <Spinner color="blaze.blaze" />
            </Flex>
          ) : (
            <>
              {isSmall && !isSearchV2 && <GlobalSearch />}
              {isSmall && isSearchV2 && (
                <Flex height="full" alignItems="flex-end" ref={mobileSearchToggleRef}>
                  <Button
                    aria-label={isMobileSearchOpen ? "Close search" : "Search"}
                    variant="navbar"
                    onClick={() => setIsMobileSearchOpen((v) => !v)}
                    {...(isMobileSearchOpen && {
                      height: "56px",
                      borderBottomRadius: 0,
                      bg: "transparent.white.10",
                      margin: 0,
                      pb: "8px",
                    })}
                  >
                    <SearchIcon w={6} h={6} color="neutral.white" />
                  </Button>
                </Flex>
              )}
              {userData && <NotificationMenu />}
              {userData && <ConversationMenu />}
              <AccountMenu />
              {!userData && (
                <Flex alignItems="center">
                  <LoginMenu />
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Flex>
      {isSmall && isSearchV2 && isMobileSearchOpen && (
        <Flex
          ref={mobileSearchRef}
          px={3}
          py={2}
          background="charcoal.charcoal"
          borderBottom="1px solid"
          borderColor="transparent.white.10"
        >
          <SearchBarWithDropdown />
        </Flex>
      )}
      {shouldShowRibbon && (
        <Box
          position="relative"
          width="100%"
          overflow="hidden"
          transition="transform 0.3s ease-out, max-height 0.3s ease-out"
          transform={isAnimatingOut ? "translateY(-100%)" : "translateY(0)"}
          maxHeight={isAnimatingOut ? "0" : "200px"}
          zIndex={isAnimatingOut ? -1 : 1}
        >
          <HtmlParser htmlString={ribbonContent!} width="100%" dontStyle />
          <IconButton
            aria-label="Dismiss ribbon"
            icon={<CloseIcon />}
            onClick={handleDismissRibbon}
            position="absolute"
            top="50%"
            right={isSmall ? "8px" : "16px"}
            transform="translateY(-50%)"
            size="sm"
            variant="ghost"
            color="white"
            _hover={{
              bg: "transparent.white.10",
            }}
            isDisabled={isAnimatingOut}
          />
        </Box>
      )}
    </Flex>
  );

  return navBarContent;
}
